import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Exclamation } from "components/icons/Exclamation";
import dayjs from "dayjs";
import { FormikValues } from "formik";
import { broadcastCallValidationSchema } from "formValidations/broadcastCallSchema";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import TaskSuccessfulTick from "static/images/TaskSuccessfulTick.svg";
import { useCsvTimeEstimationAndValidateMutation } from "store/apis/broadcastCall";
import { BackendError } from "types/utils/error";
import { IVRTestModal } from "./IVRTestModal";
import { BROADCAST_TEMPLATE_TYPE } from "constants/broadcast";

const getAvailableTimeSlots = (selectedDate: string) => {
  const timeSlots = [];
  const currentDate = dayjs();
  const selectedDateObj = dayjs(selectedDate);

  const isToday = selectedDateObj.isSame(currentDate, "day");

  let startHour = 0;

  if (isToday) {
    startHour = currentDate.hour() + 1;
  }

  for (let hour = startHour; hour <= 23; hour++) {
    timeSlots.push({
      value: hour.toString().padStart(2, "0"),
      label: `${hour.toString().padStart(2, "0")}:00`,
    });
  }

  return timeSlots;
};

const checkValuesChanged = (
  currentValue: {
    ivr_message: string;
    scheduled_date: string;
    slot: string;
  },
  validatedValue: {
    message_text: string;
    csv_file: File | null;
    scheduled_date: string;
    start_time: string;
  }
) => {
  if (currentValue.ivr_message !== validatedValue.message_text) {
    return true;
  }

  if (currentValue.scheduled_date !== validatedValue.scheduled_date) {
    return true;
  }
  if (currentValue.slot + ":00" !== validatedValue.start_time) {
    return true;
  }
  return false;
};

export const BroadcastCallConfig = ({
  uploadedCSV,
  handleGoBack,
  setTemplateFields,
  currentTemplateType,
  selectedStatesLength,
  setCurrentTemplateType,
  openSendBroadcast,
  templateFields,
}: {
  uploadedCSV: File | null;
  handleGoBack: () => void;
  currentTemplateType: number;
  selectedStatesLength: number;
  setCurrentTemplateType: (val: number) => void;
  setTemplateFields: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, any> | string>>
  >;
  openSendBroadcast: () => void;
  templateFields: Record<string, Record<string, any> | string>;
}) => {
  const { alertToast, successToast } = useToast();
  const [validateTimeSlot, { data, isLoading, isSuccess, originalArgs }] =
    useCsvTimeEstimationAndValidateMutation();
  const testMessage = useModal();

  const handleValidate = async (values: FormikValues) => {
    try {
      const { scheduled_date, slot, ivr_message } = values;
      setTemplateFields((prev) => ({
        ...prev,
        [BROADCAST_TEMPLATE_TYPE.ivrCall]: values,
      }));
      const { slot_available } = await validateTimeSlot({
        message_text: ivr_message,
        csv_file: uploadedCSV,
        scheduled_date,
        start_time: slot + ":00",
      }).unwrap();

      if (slot_available) {
        successToast({
          title: "Validation completed",
          message: "Validated and time slots estimated",
        });
      } else {
        alertToast({
          title: "Validation failed",
          message: "Time slots not available",
        });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const openTestMessage = () => {
    testMessage.open();
  };

  const handleSubmit = (values: Record<string, any>) => {
    setTemplateFields((prev) => ({
      ...prev,
      [BROADCAST_TEMPLATE_TYPE.ivrCall]: values,
    }));
    if (currentTemplateType < selectedStatesLength) {
      setCurrentTemplateType(currentTemplateType + 1);
    } else {
      openSendBroadcast();
    }
  };

  const initialValues = templateFields[
    BROADCAST_TEMPLATE_TYPE.ivrCall
  ] as Record<string, string>;

  return (
    <FormikForm
      initialValues={{
        scheduled_date: initialValues.scheduled_date,
        slot: initialValues.slot,
        ivr_message: initialValues.ivr_message,
        scheduled_time_start: data?.start_time || "",
        scheduled_time_end: data?.end_time || "",
      }}
      validationSchema={broadcastCallValidationSchema}
      onSubmit={handleValidate}
      validateOnChange
      enableReinitialize
    >
      {({ isValid, values, submitForm }) => {
        const availableTimeSlots = getAvailableTimeSlots(values.scheduled_date);

        const isValueChanged =
          originalArgs && checkValuesChanged(values, originalArgs);

        let note = null;

        if (isSuccess) {
          if (data.slot_available) {
            note = `Time slot: ${dayjs(data.start_time, "HH:mm").format(
              "hh:mm A"
            )} - ${dayjs(data.end_time, "HH:mm").format("hh:mm A")}`;
          } else {
            note = (
              <span className="t-text-body-sm t-text-red">
                Time slot is not available for this start time
              </span>
            );
          }
        }

        if (isValueChanged) {
          note = null;
        }

        return (
          <>
            <Modal.Body className="t-flex t-flex-col t-gap-6 t-overflow-auto t-h-[500px]">
              <div className="t-flex t-flex-col t-gap-4">
                {isSuccess && data?.slot_available && (
                  <div className="t-flex t-gap-1.5 t-p-2.5 t-rounded-lg t-bg-green-0 t-border-green-10 t-items-center t-border-solid">
                    <img
                      src={TaskSuccessfulTick}
                      alt="TaskSuccessfulTick"
                      className="t-size-4"
                    />
                    <div className="t-text-subtext-sm t-text-text-100">
                      Validation successful
                    </div>
                  </div>
                )}
                {isSuccess && !data?.slot_available && (
                  <div className="t-flex t-gap-1.5 t-p-2.5 t-rounded-lg t-bg-red-0 t-border-red-10 t-items-center t-border-solid">
                    <span className="t-text-red">
                      <Exclamation />
                    </span>
                    <div className="t-text-subtext-sm t-text-text-100 t-mt-1">
                      Validation failed
                    </div>
                  </div>
                )}
                <div className="t-text-subtext t-text-text-100">
                  Set date & time
                </div>
                <div className="t-flex t-gap-6">
                  <div className="t-flex-1">
                    <DateField
                      name="scheduled_date"
                      dateProps={{
                        required: true,
                        label: "Select date",
                        portalId: "scheduled_date",
                        minDate: new Date(),
                      }}
                    />
                  </div>
                  <div className="t-flex-1">
                    <Combobox
                      name="slot"
                      withForm
                      isClearable={false}
                      label="Select start time"
                      options={availableTimeSlots}
                      block
                      maxMenuHeight={200}
                      menuPlacement="auto"
                      note={note}
                    />
                  </div>
                </div>
              </div>
              <div className="t-flex t-flex-col t-gap-4">
                <div className="t-flex t-justify-between t-items-center">
                  <p className="t-text-subtitle-lg t-text-text-100 t-m-0">
                    Message
                  </p>
                  <Button
                    type="button"
                    onClick={openTestMessage}
                    disabled={!values.ivr_message}
                  >
                    Test Message
                  </Button>
                </div>
                <Banner
                  type="info"
                  description="Want to personalise your message? Use {{team_member_name}}, or {{company_name}}, or {{entity_name}} as variable and the call will say the actual name!"
                />
                <TextArea
                  block
                  placeholder="Type your message"
                  name="ivr_message"
                  label="What would you like the IVR to say?"
                />
              </div>
              <IVRTestModal {...testMessage} messageText={values.ivr_message} />
            </Modal.Body>
            <Modal.Footer className="t-flex t-justify-end t-gap-2">
              <Button type="button" onClick={handleGoBack}>
                Go back
              </Button>
              {data?.slot_available && !isValueChanged ? (
                <Button
                  type="button"
                  customType="primary"
                  onClick={() => handleSubmit(values)}
                >
                  {selectedStatesLength === currentTemplateType
                    ? "Confirm details"
                    : "Next"}
                </Button>
              ) : (
                <Button
                  type="button"
                  customType="primary"
                  disabled={!isValid || isLoading}
                  onClick={submitForm}
                  isLoading={isLoading}
                >
                  Validate
                </Button>
              )}
            </Modal.Footer>
          </>
        );
      }}
    </FormikForm>
  );
};

BroadcastCallConfig.displayName = "BroadcastCallConfig";
